import { useState, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'

const MAX_ATTEMPTS = 5
const LOCKOUT_MINUTES = 15

export default function Verify2FAPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [code, setCode] = useState('')
  const [useBackup, setUseBackup] = useState(false)
  const [backupCode, setBackupCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [attempts, setAttempts] = useState(0)
  const [lockedUntil, setLockedUntil] = useState(null)
  const [timeLeft, setTimeLeft] = useState(0)

  // Lockout countdown
  useEffect(() => {
    if (!lockedUntil) return
    const interval = setInterval(() => {
      const left = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000))
      setTimeLeft(left)
      if (left === 0) {
        setLockedUntil(null)
        setAttempts(0)
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [lockedUntil])

  const isLocked = lockedUntil && Date.now() < lockedUntil

  const verifyTotp = useCallback(async () => {
    if (isLocked) return
    if (code.length !== 6) return setError('Enter the 6-digit code from your authenticator app')
    setLoading(true)
    setError('')

    try {
      const { data: factors } = await supabase.auth.mfa.listFactors()
      const totp = factors?.totp?.[0]
      if (!totp) throw new Error('No 2FA factor found. Contact support.')

      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId: totp.id })
      if (cErr) throw cErr

      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId: totp.id,
        challengeId: challenge.id,
        code,
      })

      if (vErr) {
        const newAttempts = attempts + 1
        setAttempts(newAttempts)
        if (newAttempts >= MAX_ATTEMPTS) {
          const lockoutEnd = Date.now() + LOCKOUT_MINUTES * 60 * 1000
          setLockedUntil(lockoutEnd)
          setTimeLeft(LOCKOUT_MINUTES * 60)
          setError(`Too many failed attempts. Try again in ${LOCKOUT_MINUTES} minutes.`)
        } else {
          setError(`Invalid code. ${MAX_ATTEMPTS - newAttempts} attempt${MAX_ATTEMPTS - newAttempts !== 1 ? 's' : ''} remaining.`)
        }
        return
      }

      const destination = location.state?.from?.pathname ?? '/home'
      navigate(destination, { replace: true })
    } catch (e) {
      setError(e.message ?? 'Verification failed')
    } finally {
      setLoading(false)
    }
  }, [code, attempts, isLocked, location, navigate])

  const verifyBackupCode = useCallback(async () => {
    if (!backupCode.trim()) return setError('Enter a backup code')
    setLoading(true)
    setError('')

    try {
      const cleaned = backupCode.trim().toUpperCase()
      const encoder = new TextEncoder()
      const buf = await crypto.subtle.digest('SHA-256', encoder.encode(cleaned))
      const hash = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')

      const { data: { user } } = await supabase.auth.getUser()
      const { data: record, error: qErr } = await supabase
        .from('totp_backup_codes')
        .select('id, used_at')
        .eq('user_id', user.id)
        .eq('code_hash', hash)
        .maybeSingle()

      if (qErr || !record) throw new Error('Invalid backup code')
      if (record.used_at) throw new Error('This backup code has already been used')

      await supabase
        .from('totp_backup_codes')
        .update({ used_at: new Date().toISOString() })
        .eq('id', record.id)

      const destination = location.state?.from?.pathname ?? '/home'
      navigate(destination, { replace: true })
    } catch (e) {
      setError(e.message ?? 'Invalid backup code')
    } finally {
      setLoading(false)
    }
  }, [backupCode, location, navigate])

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="bg-surface rounded-2xl shadow-sm border border-border p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-text-primary">Two-factor authentication</h1>
          <p className="text-sm text-text-muted mt-1">
            {useBackup ? 'Enter one of your backup codes' : 'Enter the code from your authenticator app'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>
        )}

        {isLocked ? (
          <div className="text-center py-4">
            <p className="text-text-secondary text-sm">
              Account temporarily locked. Try again in{' '}
              <span className="font-semibold">
                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
              </span>
            </p>
          </div>
        ) : useBackup ? (
          <div className="space-y-3">
            <input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value)}
              placeholder="XXXX-XXXX"
              className="w-full border border-border rounded-xl px-4 py-3 text-center font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button
              onClick={verifyBackupCode}
              disabled={loading}
              className="w-full bg-primary text-white rounded-xl py-3 font-semibold text-sm disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Use backup code'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              autoFocus
              className="w-full border border-border rounded-xl px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <button
              onClick={verifyTotp}
              disabled={loading || code.length !== 6}
              className="w-full bg-primary text-white rounded-xl py-3 font-semibold text-sm disabled:opacity-50"
            >
              {loading ? 'Verifying…' : 'Verify'}
            </button>
          </div>
        )}

        <button
          onClick={() => { setUseBackup(!useBackup); setError(''); setCode(''); setBackupCode('') }}
          className="w-full mt-3 text-sm text-text-muted hover:text-text-secondary underline underline-offset-2"
        >
          {useBackup ? 'Use authenticator app instead' : 'Use a backup code instead'}
        </button>
      </div>
    </div>
  )
}
